import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import * as Sentry from "../lib/sentry";
import store from "../store";
import { updateAccessToken, sessionExpired } from "../features/auth/authSlice";
import { auth } from "../utils/firebase";

// Retry configuration for 429 (rate limit) errors
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelayMs: 1000, // Start with 1 second
  maxDelayMs: 10000, // Cap at 10 seconds
};

// Extend AxiosRequestConfig to track retry count
interface RetryableAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retryCount?: number;
}

// Sleep helper for exponential backoff
const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

// Calculate delay with exponential backoff + jitter
const getRetryDelay = (retryCount: number): number => {
  const exponentialDelay = RETRY_CONFIG.baseDelayMs * Math.pow(2, retryCount);
  const jitter = Math.random() * 500; // Add 0-500ms jitter to prevent thundering herd
  return Math.min(exponentialDelay + jitter, RETRY_CONFIG.maxDelayMs);
};

// In dev mode with Vite proxy, use relative URL ("/api")
// In production or with explicit URL, use that URL
const getBaseUrl = () => {
  const envUrl = import.meta.env.VITE_APP_API_BASE_URL;

  // If env var is explicitly set (even to empty string), check its value
  if (envUrl !== undefined && envUrl !== null) {
    // Empty string or "/" means use relative URL (for Vite proxy)
    if (envUrl === "" || envUrl === "/") {
      return "";
    }
    // Remove trailing slash if present
    return envUrl.endsWith('/') ? envUrl.slice(0, -1) : envUrl;
  }

  // Fallback: production URL so a missing env var never breaks the live site
  return "https://api.fairday.app";
};

export const ApiInstance = axios.create({
  baseURL: getBaseUrl(),
  timeout: 30000,
  withCredentials: true, // Send cookies with every request
});

// Helper to get user-friendly error messages
export const getErrorMessage = (error: AxiosError & { code?: string }): string => {
  if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
    return "Request timed out. Please check your connection and try again.";
  }
  if (!navigator.onLine) {
    return "You appear to be offline. Please check your internet connection.";
  }
  if (error.response) {
    const status = error.response.status;
    if (status === 400) return "Invalid request. Please check your information and try again.";
    if (status === 401) return "Your session has expired. Please sign in again.";
    if (status === 403) return "You don't have permission to perform this action.";
    if (status === 404) return "The requested resource was not found.";
    if (status === 429) return "Too many requests. Please wait a moment and try again.";
    if (status >= 500) return "Server error. Please try again later.";
  }
  return "Something went wrong. Please try again.";
};

ApiInstance.interceptors.request.use(
  (config) => {
    // Prefix /api to the URL if it's an absolute path relative to host
    if (config.url && config.url.startsWith('/') && !config.url.startsWith('/api/')) {
      config.url = `/api${config.url}`;
    } else if (config.url && !config.url.startsWith('http') && !config.url.startsWith('api/') && !config.url.startsWith('/api/')) {
      config.url = `api/${config.url}`;
    }

    const state = store.getState();
    if (state && state.auth.accessToken) {
      config.headers.Authorization = `Bearer ${state.auth.accessToken}`;
    }

    // Attach CSRF token if backend provides one via cookie
    const csrfToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('csrf_token='))
      ?.substring('csrf_token='.length);
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }

    return config;
  },
  (error) => {
    if (import.meta.env.DEV) {
      console.error("Request interceptor error");
    }
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

ApiInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (!error.response) {
      // Network error / timeout — report to Sentry
      Sentry.captureException(error, { tags: { source: 'axios', type: 'network' } });
      return Promise.reject(error);
    }

    const status = error.response.status;
    const oRequest = error.config as RetryableAxiosRequestConfig;

    // Report server errors (5xx) to Sentry
    if (status >= 500) {
      Sentry.captureException(error, {
        tags: { source: 'axios', statusCode: String(status) },
        extra: { url: oRequest?.url, method: oRequest?.method },
      });
    }

    // Handle 429 (rate limit) with exponential backoff retry
    if (status === 429 && oRequest) {
      const retryCount = oRequest._retryCount || 0;

      if (retryCount < RETRY_CONFIG.maxRetries) {
        oRequest._retryCount = retryCount + 1;
        const delay = getRetryDelay(retryCount);

        if (import.meta.env.DEV) {
          console.log(`Rate limited (429). Retrying in ${Math.round(delay)}ms (attempt ${retryCount + 1}/${RETRY_CONFIG.maxRetries})`);
        }

        await sleep(delay);
        return ApiInstance(oRequest);
      }

      // Max retries exceeded — log and reject
      if (import.meta.env.DEV) {
        console.error(`Rate limit retry failed after ${RETRY_CONFIG.maxRetries} attempts:`, oRequest.url);
      }
    }

    // Only attempt refresh on 401 (expired access token)
    // Do not attempt refresh for auth endpoints (login/register/social-login/refresh-token)
    // WARNING: If refresh-token is not excluded, it will trigger an infinite promise deadlock!
    const isAuthEndpoint = oRequest?.url?.includes('/consumers/auth/login') || 
                           oRequest?.url?.includes('/consumers/auth/social-login') || 
                           oRequest?.url?.includes('/consumers/auth/register') ||
                           oRequest?.url?.includes('/consumers/auth/refresh-token');

    if (status === 401 && !oRequest._retry && !isAuthEndpoint) {
      oRequest._retry = true;

      // Deduplicate concurrent refresh calls
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = (async () => {
          let idToken: string | undefined = undefined;
          try {
            await auth.authStateReady(); // Wait for Firebase to initialize
            if (auth.currentUser) {
              idToken = await auth.currentUser.getIdToken(true);
            }
          } catch (e) {
            console.warn("Failed to get Firebase idToken for refresh fallback", e);
          }

          return ApiInstance.post("/consumers/auth/refresh-token", idToken ? { idToken } : undefined)
            .then((res) => {
              const { accessToken } = res.data;
              store.dispatch(updateAccessToken(accessToken));
              return accessToken;
            })
            .catch((refreshError) => {
              // Refresh failed – clear only the token, not the entire auth state.
              // This prevents the UI from flashing to logged-out on transient
              // network issues. A true logout is only triggered by explicit user
              // action or a 401 on the refresh endpoint itself with a valid response.
              if (refreshError.response?.status === 401) {
                // Server explicitly rejected the refresh token — real session expiry
                store.dispatch(updateAccessToken(null));
              } else {
                // Network error or other transient failure — keep user context
                store.dispatch(sessionExpired());
              }
              throw refreshError;
            })
            .finally(() => {
              isRefreshing = false;
              refreshPromise = null;
            });
        })();
      }

      try {
        const newAccessToken = await refreshPromise!;
        oRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return ApiInstance(oRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);
